import { pressable } from '../pressable';
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import AppBackground from './AppBackground';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';
import PolaroidStacks from './PolaroidStacks';
import PhoneMockup from './PhoneMockup';
import ThemeToggle from './ThemeToggle';

// Add VideoPlayerOverlay component for in-screen video playback
const VideoPlayerOverlay = ({ videoUrl, onClose }: { videoUrl: string; onClose: () => void }) => {
  // Extract YouTube video ID from URL
  const getYouTubeVideoId = (url: string) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const videoId = getYouTubeVideoId(videoUrl);

  if (!videoId) return null;

  // Calculate embedUrl
  const embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&controls=1&showinfo=1&playsinline=1`;

  // Create a portal to render directly to body
  return ReactDOM.createPortal(
    <motion.div 
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh'
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div 
        className="w-full h-full flex items-center justify-center"
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
      >
        <button 
          className="absolute top-8 right-8 z-10 p-3 bg-black/70 hover:bg-black/90 rounded-full text-white/80 hover:text-white transition-colors"
          aria-label="Close"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
        >
          <X size={28} />
        </button>
        <iframe
          className="w-full h-full max-h-screen"
          src={embedUrl}
          title="YouTube video player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        ></iframe>
      </motion.div>
    </motion.div>,
    document.body
  );
};

interface DesktopOverlayProps {
  onClose: () => void;
}

const DesktopOverlay: React.FC<DesktopOverlayProps> = ({ onClose }) => {
  const isLoaded = true;
  // Add state for video URL
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  // Add state for 'shipped by' intro animation
  const [showShippedBy, setShowShippedBy] = useState(true);

  // Hide 'shipped by' after 3 seconds on first visit
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setShowShippedBy(false);
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  // Add function to handle video links
  const handleVideoLink = (url: string) => {
    setVideoUrl(url);
  };

  // Function to close video player
  const closeVideoPlayer = () => {
    setVideoUrl(null);
  };

  const contentBeforeLoveList = `\
<span style="font-size: clamp(20px, 4vw, 24px); font-weight: 500; line-height: 1.3;">Hey, I\'m Fareeha 🤍</span>

You might\'ve found me through one of my Partifuls where I made you eat an <a href="javascript:void(0)" class="custom-pink-link custom-pink-link--desktop-overlay" onclick="event.stopPropagation(); window.desktopOpenInPhone({app:\'notes\', noteId:3})">apple you\'d never heard of</a>. I feel half the fun of finding something great, is sharing it.

These days, I\'m mostly sharing <a href="javascript:void(0)" class="custom-pink-link custom-pink-link--desktop-overlay" onclick="event.stopPropagation(); window.desktopOpenInPhone({app:\'notes\', noteId:8})">what I\'m learning about superintelligence</a>, and I\'ve just joined <a href="https://www.mts.now" target="_blank" rel="noopener noreferrer" class="custom-pink-link custom-pink-link--desktop-overlay" onclick="event.stopPropagation()">MTS</a>, a team that helps the world make sense of it.

<span style="font-weight: bold;">other loves:</span>
▹ pilates with friends (my app, <a href="https://kineship.com" target="_blank" rel="noopener noreferrer" class="custom-pink-link custom-pink-link--desktop-overlay" onclick="event.stopPropagation()">Kineship</a>)
▹ walking <a href="javascript:void(0)" class="custom-pink-link custom-pink-link--desktop-overlay" onclick="event.stopPropagation(); window.desktopOpenInPhone({app:\'notes\', noteId:9})">almost anywhere</a>
▹ wearing one too many <a href="javascript:void(0)" class="custom-pink-link custom-pink-link--desktop-overlay" onclick="event.stopPropagation(); window.desktopOpenInPhone({app:\'notes\', noteId:7})">wearables</a> at once

If this feels like your kind of world, <a href="mailto:fareeha@kineship.com" class="custom-pink-link custom-pink-link--desktop-overlay" onclick="event.stopPropagation()">say hi</a> 💛
`;

  const contentAfterLoveList = ``;

  // Links in the card open the matching screen in the phone mockup; if the phone
  // isn't shown (narrow window), switch to the full app view and open it there
  React.useEffect(() => {
    window.desktopOpenInPhone = (target) => {
      const frame = document.querySelector<HTMLIFrameElement>('iframe[title="Fareeha OS on a phone"]');
      if (frame && frame.offsetParent !== null && frame.contentWindow) {
        frame.contentWindow.postMessage({ type: 'open', ...target }, window.location.origin);
      } else {
        onClose();
        setTimeout(() => (window as any).__openInApp?.(target), 600);
      }
    };
    return () => {
      delete window.desktopOpenInPhone;
    };
  }, [onClose]);

  // Make handleVideoLink available globally within the component's lifecycle
  React.useEffect(() => {
    // Expose the handleVideoLink function globally
    window.desktopHandleVideoLink = handleVideoLink;
    
    // Clean up when component unmounts
    return () => {
      delete window.desktopHandleVideoLink;
    };
  }, []);

  return (
    <motion.div
      className="fixed inset-0 flex items-center justify-center p-4 sm:p-8 z-50 overflow-y-auto"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <AppBackground isLoaded={isLoaded} />

      <PolaroidStacks />

      <div className="fixed top-5 right-6 z-[60]">
        <ThemeToggle />
      </div>
      
      <div className="relative z-10 flex items-center justify-center gap-16 w-full max-w-6xl mx-auto my-auto">
      <div className="relative w-full max-w-4xl [@media(min-width:1100px)_and_(min-height:680px)]:max-w-[600px]">
        <motion.h2 
          className="absolute top-[-24px] right-9 text-[18px] font-semibold text-white z-20"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.9, ease: [0.25, 0.8, 0.25, 1] } }}
          style={{ 
            textShadow: '0px 1px 1px rgba(0, 0, 0, 0.3)' 
          }}
        >
          <span className="flex items-center">
            <a href="https://github.com/fareeha-s" target="_blank" rel="noopener noreferrer" onClick={(e) => { e.stopPropagation(); window.open('https://github.com/fareeha-s', '_blank'); }} style={{ cursor: 'pointer' }} className="github-link inline-block relative group">
              <img 
                src="./icons/hosts/fareeha.jpg" 
                alt="Fareeha" 
                className="rounded-full w-12 h-12 object-cover border border-white/20" 
                style={{ 
                  zIndex: 10002,
                  boxShadow: '0 1px 2px rgba(0,0,0,0.15), 0 0 1px rgba(255,255,255,0.2) inset',
                  backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%)',
                  transform: 'translateZ(0)',
                  transition: 'transform 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94), box-shadow 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94), border-color 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)' 
                }}
                onTouchStart={(e) => {
                  e.currentTarget.style.transform = 'scale(0.97) translateZ(0)';
                  e.currentTarget.style.boxShadow = '0 0px 1px rgba(0,0,0,0.1), 0 0 1px rgba(255,255,255,0.1) inset';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                }}
                onTouchEnd={(e) => {
                  e.currentTarget.style.transform = 'translateZ(0)';
                  e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.15), 0 0 1px rgba(255,255,255,0.2) inset';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05) translateZ(0)';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2), 0 0 1px rgba(255,255,255,0.3) inset';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateZ(0)';
                  e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.15), 0 0 1px rgba(255,255,255,0.2) inset';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                }}
              />
              <svg 
                width="12" 
                height="12" 
                viewBox="0 0 24 24" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg" 
                style={{
                  stroke: 'white', 
                  strokeWidth: 2.5, 
                  position: 'absolute',
                  top: '-2px',
                  right: '-2px',
                  zIndex: 99999,
                  pointerEvents: 'none' // Allow clicks to pass through to the <a> tag
                }}
              >
                  <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" />
                </svg>
            </a>
          </span>
        </motion.h2>

        <motion.div
          className="desktop-card relative w-full max-w-4xl mx-auto bg-white/8 border border-white/15 rounded-[28px] shadow-lg p-6 sm:p-8 md:p-12 overflow-y-auto max-h-[85vh] z-10"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          style={{
            backdropFilter: 'blur(28px) saturate(160%)',
            WebkitBackdropFilter: 'blur(28px) saturate(160%)',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37), inset 0 1px 0 0 rgba(255, 255, 255, 0.15)',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)'
          }}
        >
          <div
            className="text-settle text-base sm:text-lg leading-relaxed text-white/90 prose prose-invert max-w-none lora-note-content font-medium"
            style={{ whiteSpace: 'pre-line', fontSize: 'clamp(0.95rem, 2vw, 1.15rem)' }}
          >
            {/* Render content before list */}
            <div dangerouslySetInnerHTML={{ __html: contentBeforeLoveList }} />

            {/* Render content after list (now empty) */}
            {contentAfterLoveList && (
                 <div
                    style={{ marginTop: '1.5em' }}
                    dangerouslySetInnerHTML={{ __html: contentAfterLoveList }}
                />
            )}
          </div>
        </motion.div>

        {/* 'Fold this screen' text moved outside and below the main content container */}
        <motion.div 
          className="[@media(min-width:1100px)_and_(min-height:680px)]:hidden"
          style={{ marginTop: 'clamp(1em, 2vh, 1.5em)' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.6 }}
        >
          <span
            onClick={onClose}
            {...pressable()}
            style={{ 
              opacity: 0.7, 
              fontWeight: 'normal', 
              fontSize: 'clamp(0.8em, 1.5vw, 0.9em)',
              cursor: 'pointer',
              transition: 'opacity 0.2s ease, transform 0.2s ease'
            }}
            className="flex items-center justify-center hover:opacity-70 active:opacity-90"
            onMouseOver={(e) => {
              e.currentTarget.style.opacity = '1';
              e.currentTarget.style.transform = 'scale(1.02)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.opacity = '0.7';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            Click here to turn this site into an app.
          </span>
        </motion.div>
      </div>

        {/* Live phone running the mobile app, shown when there's room */}
        <motion.div
          className="hidden [@media(min-width:1100px)_and_(min-height:680px)]:block shrink-0"
          initial={{ y: 24, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.35, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <PhoneMockup src="/?phone=1" />
        </motion.div>
      </div>

      {/* Video player overlay */}
      <AnimatePresence>
        {videoUrl && (
          <VideoPlayerOverlay 
            videoUrl={videoUrl} 
            onClose={closeVideoPlayer} 
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Add type declaration for global window properties
declare global {
  interface Window {
    desktopHandleVideoLink?: (url: string) => void;
    desktopOpenInPhone?: (target: { app: string; noteId?: number }) => void;
  }
}

export default DesktopOverlay; 